import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "../components/ui/select";
import { UserPlus, Mail, ShieldOff, Send, RefreshCcw, Copy, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function statusChip(status) {
  const map = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    deactivated: "bg-slate-100 text-slate-500 border-slate-200",
  };
  return `text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${map[status] || "bg-slate-50 text-slate-500 border-slate-200"}`;
}

export default function ExternalUsersPage() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openInvite, setOpenInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteCompany, setInviteCompany] = useState("");
  const [inviteRole, setInviteRole] = useState("EXTERNAL");
  const [inviting, setInviting] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get("/admin/external-users"),
        api.get("/companies"),
      ]);
      setUsers(u.data || []);
      setCompanies((c.data || []).filter(x => x.type === "THIRD_PARTY" || x.type === "SKYWORTH"));
    } catch (e) {
      toast.error("Failed to load external users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitInvite = async () => {
    if (!inviteEmail || !inviteCompany) { toast.error("Email and company are required"); return; }
    setInviting(true);
    setLastInvite(null);
    try {
      const { data } = await api.post("/admin/external-users/invite", {
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim(),
        company_id: inviteCompany,
        role: inviteRole,
      });
      setLastInvite(data);
      if (data.email_delivery?.status === "sent") {
        toast.success(`Invitation emailed to ${data.email}`);
      } else {
        toast.warning("Invite created — email delivery failed. Use the copy-link button below.");
      }
      setInviteEmail(""); setInviteName(""); setInviteCompany(""); setInviteRole("EXTERNAL");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to invite");
    } finally {
      setInviting(false);
    }
  };

  const resend = async (email) => {
    try {
      const { data } = await api.post(`/admin/external-users/${encodeURIComponent(email)}/resend`);
      if (data.email_delivery?.status === "sent") toast.success(`Invitation resent to ${email}`);
      else toast.warning("New link created, email delivery failed");
      setLastInvite({ email, setup_url: data.setup_url, email_delivery: data.email_delivery });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
  };

  const toggleActive = async (u) => {
    const action = u.status === "deactivated" ? "reactivate" : "deactivate";
    try {
      await api.post(`/admin/external-users/${encodeURIComponent(u.email)}/${action}`);
      toast.success(`User ${action}d`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  return (
    <div className="p-6 space-y-6" data-testid="external-users-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-heading text-slate-800">External Users</h1>
          <p className="text-sm text-slate-500 mt-1">
            Invite Skyworth customers and 3PA contacts. They receive an email with a setup link
            and can only see job cards belonging to their company.
          </p>
        </div>
        <Button
          data-testid="invite-external-btn"
          onClick={() => setOpenInvite(true)}
          className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
        ><UserPlus size={16} className="mr-2" /> Invite user</Button>
      </div>

      {lastInvite?.setup_url && (
        <div data-testid="last-invite-banner" className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-900 text-sm">Setup link</p>
            <p className="text-xs text-amber-800 mt-1 break-all">{lastInvite.setup_url}</p>
            {lastInvite.email_delivery?.error && (
              <p className="text-[11px] text-amber-700 mt-1">Email delivery: {lastInvite.email_delivery.error}</p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => copyLink(lastInvite.setup_url)} data-testid="copy-link-btn">
            <Copy size={14} className="mr-1" /> Copy
          </Button>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-sm" data-testid="external-users-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Invited</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-8">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-8">No external users yet. Click "Invite user" to start.</td></tr>
            ) : users.map(u => (
              <tr key={u.email} data-testid={`row-${u.email}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{u.name || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3 text-slate-600">{u.company_name || "—"}</td>
                <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{u.role}</span></td>
                <td className="px-4 py-3"><span className={statusChip(u.status)} data-testid={`status-${u.email}`}>{u.status}</span></td>
                <td className="px-4 py-3 text-slate-500 text-xs">{u.invited_at ? new Date(u.invited_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  {u.status === "pending" && (
                    <Button size="sm" variant="outline" onClick={() => resend(u.email)} data-testid={`resend-${u.email}`}>
                      <RefreshCcw size={12} className="mr-1" /> Resend
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleActive(u)}
                    data-testid={`toggle-${u.email}`}
                    className={u.status === "deactivated" ? "" : "text-red-600 hover:text-red-700"}
                  >
                    {u.status === "deactivated" ? <ShieldCheck size={12} className="mr-1" /> : <ShieldOff size={12} className="mr-1" />}
                    {u.status === "deactivated" ? "Reactivate" : "Deactivate"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={openInvite} onOpenChange={setOpenInvite}>
        <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={16} /> Invite external user</DialogTitle>
            <DialogDescription>They'll receive an email with a link to set their password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Email address</Label>
              <Input data-testid="invite-email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="customer@example.com" />
            </div>
            <div>
              <Label className="text-xs">Full name (optional)</Label>
              <Input data-testid="invite-name" value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Jane Customer" />
            </div>
            <div>
              <Label className="text-xs">Company</Label>
              <Select value={inviteCompany} onValueChange={setInviteCompany}>
                <SelectTrigger data-testid="invite-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-slate-400 text-xs">({c.type})</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EXTERNAL">EXTERNAL — read-only customer view</SelectItem>
                  <SelectItem value="3PA">3PA — third-party agent (full agent perms)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenInvite(false)}>Cancel</Button>
            <Button onClick={submitInvite} disabled={inviting} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="invite-submit-btn">
              <Send size={14} className="mr-2" />{inviting ? "Sending…" : "Send invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
